import { useCallback } from 'react';

import { Checkbox } from '@/lib/base/forms/checkbox';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';

import { SNOWFLAKE_V1_TYPES } from '../snowflake-selection';

const PLURAL_LABEL = { plural: true } as const;

const SHARE_ALL_DESCRIPTION = 'All records shared.';
const SHARE_ORG_VISIBLE_DESCRIPTION =
  'Org-visible records only. Private records are never exported.';

const NO_AVAILABLE_TYPES_NOTE =
  'This organization has no record types available to share via Snowflake.';

interface SnowflakeTypeRowProps {
  entityTypeName: string;
  label: string;
  carriesPrivacy: boolean;
  checked: boolean;
  disabled?: boolean;
  onToggle: (entityTypeName: string, checked: boolean) => void;
}

function SnowflakeTypeRow({
  entityTypeName,
  label,
  carriesPrivacy,
  checked,
  disabled,
  onToggle,
}: SnowflakeTypeRowProps) {
  const handleChange = useCallback(
    (isSelected: boolean) => onToggle(entityTypeName, isSelected),
    [onToggle, entityTypeName]
  );

  return (
    <Checkbox
      isSelected={checked}
      isDisabled={disabled}
      onChange={handleChange}
      label={label}
      description={
        carriesPrivacy ? SHARE_ORG_VISIBLE_DESCRIPTION : SHARE_ALL_DESCRIPTION
      }
    />
  );
}
SnowflakeTypeRow.displayName = 'SnowflakeTypeRow';

export interface SnowflakeTypeSelectionPickerProps {
  orgId: number;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  availableTypeNames?: Set<string>;
}

export function SnowflakeTypeSelectionPicker({
  orgId,
  value,
  onChange,
  disabled,
  availableTypeNames,
}: SnowflakeTypeSelectionPickerProps) {
  // Labels come from the org's schema (the same source every CRM surface
  // uses), falling back to the built-in registry while it loads.
  const { data: objectTypesData } = useObjectTypes(orgId);
  const objectTypes = objectTypesData?.allObjectTypes;

  const handleToggle = useCallback(
    (entityTypeName: string, checked: boolean) => {
      onChange(
        checked
          ? [...value, entityTypeName]
          : value.filter(name => name !== entityTypeName)
      );
    },
    [value, onChange]
  );

  if (availableTypeNames !== undefined && availableTypeNames.size === 0) {
    return (
      <p className='text-sm text-[var(--color-text-secondary)]'>
        {NO_AVAILABLE_TYPES_NOTE}
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      {SNOWFLAKE_V1_TYPES.map(type => {
        const isAvailable =
          availableTypeNames === undefined ||
          availableTypeNames.has(type.entityTypeName);
        return (
          <SnowflakeTypeRow
            key={type.entityTypeName}
            entityTypeName={type.entityTypeName}
            label={getTypeLabelByName(
              type.entityTypeName,
              PLURAL_LABEL,
              objectTypes
            )}
            carriesPrivacy={type.carriesPrivacy}
            checked={value.includes(type.entityTypeName) && isAvailable}
            disabled={disabled || !isAvailable}
            onToggle={handleToggle}
          />
        );
      })}
    </div>
  );
}
SnowflakeTypeSelectionPicker.displayName = 'SnowflakeTypeSelectionPicker';
